import { useState, useEffect } from "react";
import api from "@/lib/api";
import { logEvent } from "@/lib/analytics";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { PhoneCall, CalendarPlus, Handshake } from "@phosphor-icons/react";

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };

const META = {
  call: { title: "Log a Call", icon: PhoneCall, desc: "Record an outbound call/contact attempt." },
  followup: { title: "Schedule Follow-Up", icon: CalendarPlus, desc: "Set the next follow-up date for this account." },
  settlement: { title: "Start Settlement", icon: Handshake, desc: "Raise a settlement request for approval." },
};

export function QuickActionDialog({ open, action, account, onOpenChange, onDone }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [disposition, setDisposition] = useState("Called");
  const [dueDate, setDueDate] = useState(tomorrow());
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setNote(""); setDisposition("Called"); setDueDate(tomorrow()); setReason("");
      setAmount(account ? String(Math.round((account.outstanding_amount || 0) * 0.7)) : "");
    }
  }, [open, account]);

  if (!account) return null;
  const meta = META[action] || META.call;
  const Icon = meta.icon;

  const submit = async () => {
    setBusy(true);
    try {
      if (action === "call") {
        await api.post(`/accounts/${account.account_id}/notes`, { note: note || "Call logged from Insights", disposition });
        toast.success(`Call logged for ${account.borrower_name}`);
      } else if (action === "followup") {
        if (!dueDate) { toast.error("Pick a follow-up date"); setBusy(false); return; }
        await api.post(`/accounts/${account.account_id}/followup`, { due_date: dueDate, note: note || "Follow-up scheduled from Insights" });
        logEvent("followup_created", { source: "insights_quick_action", account_id: account.account_id });
        toast.success(`Follow-up set for ${account.borrower_name} on ${dueDate}`);
      } else if (action === "settlement") {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) { toast.error("Enter a valid settlement amount"); setBusy(false); return; }
        await api.post(`/settlements`, { account_id: account.account_id, settlement_amount: amt, reason, notes: note });
        toast.success(`Settlement raised for ${account.borrower_name}`);
      }
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-sm" data-testid="quick-action-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Icon size={18} weight="bold" className="text-[#0B3D91]" /> {meta.title}</DialogTitle>
          <DialogDescription>{meta.desc}</DialogDescription>
        </DialogHeader>
        <div className="text-sm border border-border bg-secondary/40 rounded-sm px-3 py-2">
          <span className="font-semibold">{account.borrower_name}</span>
          <span className="text-muted-foreground"> · {account.loan_id} · {fmtINR(account.outstanding_amount)} outstanding · {account.dpd} DPD</span>
        </div>

        <div className="space-y-3 mt-1">
          {action === "call" && (
            <div>
              <Label className="text-xs">Disposition</Label>
              <select value={disposition} onChange={(e) => setDisposition(e.target.value)} className="mt-1 w-full h-9 rounded-sm border border-border bg-background px-2 text-sm" data-testid="qa-disposition">
                {["Called", "Connected", "Not Connected", "Promise To Pay", "Decline to Pay", "Wrong Number", "Busy"].map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          )}
          {action === "followup" && (
            <div>
              <Label className="text-xs">Follow-up date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="rounded-sm mt-1" data-testid="qa-due-date" />
            </div>
          )}
          {action === "settlement" && (
            <>
              <div>
                <Label className="text-xs">Settlement amount (₹)</Label>
                <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-sm mt-1" data-testid="qa-amount" />
                <p className="text-[11px] text-muted-foreground mt-1">Suggested ~70% of outstanding. Goes to manager for approval.</p>
              </div>
              <div>
                <Label className="text-xs">Reason</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Financial hardship" className="rounded-sm mt-1" data-testid="qa-reason" />
              </div>
            </>
          )}
          <div>
            <Label className="text-xs">{action === "call" ? "Notes" : "Notes (optional)"}</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a short note…" className="rounded-sm mt-1" data-testid="qa-note" />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="qa-cancel">Cancel</Button>
          <Button className="rounded-sm bg-[#0B3D91] hover:bg-[#0a3578]" disabled={busy} onClick={submit} data-testid="qa-submit">
            {busy ? "Saving…" : meta.title}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
